import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  MessageSquare,
  FileText,
  MoreVertical,
  Clock,
  ArrowLeft,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  getSpaceDetailsEnhanced,
  deleteChatFromSpace,
  deleteContractFromSpace,
  addChatToSpace,
} from '../api/api';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchChatSessions } from '../redux/slices/chatSessionsSlice';
import { useDispatch } from 'react-redux';
import { setDocument } from '../redux/slices/documentSlice';
import { handleApiError } from '../helpers/handleApiError';

export default function SpaceDetail({ isDarkMode }) {
  const { state } = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const uid = user?.id;
  const spaces = useSelector((state) => state.spaces.list);
  const spaceId = state?.space?.space_id || params?.spaceId;
  const [space, setSpace] = useState(state?.space || null);
  const [loading, setLoading] = useState(!state?.space);
  const [openMenu, setOpenMenu] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!spaceId || !uid) return;
    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const data = await getSpaceDetailsEnhanced(spaceId, uid);
        if (!cancelled) {
          setSpace(data.space || data);
        }
      } catch (err) {
        if (!cancelled) {
          handleApiError(err, 'Failed to load space detail.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [spaceId, uid, spaces]);
  const handleDropChat = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const chatId = e.dataTransfer.getData('chatId');
    if (!chatId) return;
    if (space.chat_sessions?.some((c) => c.chat_session_id === chatId)) {
      toast('This chat is already added to this space.', { icon: 'ℹ️' });
      return;
    }

    try {
      await addChatToSpace(space.space_id, chatId, uid);

      toast.success('Chat added to space');

      const updated = await getSpaceDetailsEnhanced(space.space_id, uid);
      setSpace(updated.space || updated);

      dispatch(fetchChatSessions());
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add chat');
    }
  };
  const formatName = (name, maxLength = 34) => {
    if (!name || name.length <= maxLength) return name;
    const keep = Math.floor((maxLength - 3) / 2);
    return `${name.slice(0, keep)}...${name.slice(-keep)}`;
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-[80vh] w-full">
        <div
          className={`flex flex-col items-center space-y-3 ${
            isDarkMode ? 'text-slate-300' : 'text-slate-600'
          }`}
        >
          <div
            className={`w-10 h-10 border-4 ${
              isDarkMode ? 'border-indigo-400' : 'border-brand'
            } border-t-transparent rounded-full animate-spin`}
          />
          <p className="text-base font-medium tracking-wide">
            Loading space details...
          </p>
        </div>
      </div>
    );

  if (!space) {
    return (
      <div className="p-6 text-center">
        <p className="text-lg font-semibold">No space data found.</p>
        <p className="text-sm text-gray-500 mt-1">
          Try going back and selecting the space again.
        </p>
      </div>
    );
  }
  const hasChats = space.chat_sessions?.length > 0;
  const hasDocuments = space.contracts?.length > 0;

  return (
    <div
      className={`p-6 sm:p-8 w-full h-full overflow-y-auto transition-colors ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <div className="mb-2">
            <button
              onClick={() => navigate('/app/spaces')}
              className={`group inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 shadow-sm
              ${
                isDarkMode
                  ? 'bg-gray-800 text-indigo-300 border-gray-700 hover:bg-gray-700 hover:text-white'
                  : 'bg-brand text-white'
              }`}
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            </button>
          </div>
          <h1 className="text-xl font-semibold">{space.name}</h1>

          {space.description && (
            <p
              className={`mt-1 max-w-2xl text-sm leading-relaxed ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              {space.description}
            </p>
          )}

          <div
            className={`flex items-center gap-6 text-sm mt-2 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            <div className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              {space?.chat_sessions?.length || 0} discussions
            </div>
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {space.contracts?.length || 0} docs
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        {/* <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              className={`pl-9 pr-3 py-2 w-full rounded-md border text-sm focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-400 focus:ring-indigo-500'
                  : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400 focus:ring-indigo-500'
              }`}
            />
          </div>
        </div> */}
      </div>

      {/* Two Section Layout (Cards) */}
      {/* === If both are empty, show single centered view === */}
      {!hasChats && !hasDocuments ? (
        <div
          onDragOver={(e) => {
            e.preventDefault(); // 🔥 REQUIRED
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDropChat}
          className={`flex flex-col items-center justify-center h-[40vh] text-center
    transition-all
    ${isDragOver ? 'ring-2 ring-indigo-500 bg-indigo-500/5' : ''}
  `}
        >
          <div className="p-4 rounded-full bg-brand dark:bg-brandLighter mb-4">
            <FileText className="h-10 w-10 text-brand" />
          </div>
          <h2
            className={`text-lg font-semibold mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}
          >
            Start by asking a Legal Question or upload your first document
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Drag and drop recent queries or documents to add to this space.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/app/chat')}
              className="px-7 py-2 rounded-lg bg-brand hover:bg-brand text-white text-sm font-medium shadow"
            >
              New Legal Insight
            </button>
            <button
              onClick={() => navigate('/app/document')}
              className="px-6 py-2 rounded-lg bg-brand hover:bg-brand text-white text-sm font-medium shadow"
            >
              New Document Analysis
            </button>
          </div>
        </div>
      ) : (
        // === Otherwise, show the normal two-section layout ===
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(90vh-180px)]">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDropChat}
            className={`p-5 border rounded-md md:col-span-2 transition-all
    ${isDragOver ? 'ring-2 ring-indigo-500 bg-indigo-500/5' : ''}
    ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
  `}
          >
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Recent Queries</h2>

              <hr
                className={`mt-2 ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-300'
                }`}
              />
            </div>

            {hasChats ? (
              <ul className="space-y-4">
                {space.chat_sessions.map((chat) => (
                  <li
                    key={chat.chat_session_id}
                    className={`relative p-3 border rounded-md cursor-pointer transition-all ${
                      isDarkMode
                        ? 'border-gray-700 bg-gray-900 hover:bg-gray-800'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                    onClick={() =>
                      navigate(`/app/chat/${chat.chat_session_id}`)
                    }
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-brand" />
                        <p className="font-medium text-sm">
                          {chat.title || 'Untitled Conversation'}
                        </p>
                      </div>

                      <div
                        className="relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() =>
                            setOpenMenu(
                              openMenu === chat.chat_session_id
                                ? null
                                : chat.chat_session_id
                            )
                          }
                          className="p-1 rounded hover:bg-brand/10 focus:outline-none transition"
                        >
                          <MoreVertical className="w-4 h-4 text-brand" />
                        </button>

                        <AnimatePresence>
                          {openMenu === chat.chat_session_id && (
                            <motion.div
                              role="menu"
                              aria-label="Chat Options"
                              initial={{ opacity: 0, y: -6, scale: 0.98 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: -6, scale: 0.98 }}
                              transition={{ duration: 0.18, ease: 'easeOut' }}
                              className={`absolute right-0 mt-1 w-40 rounded-md shadow-xl border z-[30] 
        ${
          isDarkMode
            ? 'bg-gray-900/90 border-gray-700 backdrop-blur-sm'
            : 'bg-white/90 border-gray-200 backdrop-blur-sm'
        }
      `}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenu(null);
                                  setConfirmDelete({
                                    chatId: chat.chat_session_id,
                                    title:
                                      chat.title || 'Untitled Conversation',
                                  });
                                }}
                                className="w-full text-left px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
hover:bg-red-500 hover:text-white
 dark:hover:text-white 
          transition-all duration-150 ease-in-out rounded-md"
                              >
                                Remove from Space
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {chat.last_message?.content && (
                      <p
                        className={`text-xs mb-2 line-clamp-2 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {chat.last_message.content.length > 120
                          ? chat.last_message.content.slice(0, 120) + '...'
                          : chat.last_message.content}
                      </p>
                    )}
                    <div
                      className={`flex items-center justify-between text-xs ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <span>
                          {chat.last_message?.role === 'ai'
                            ? 'Legal Chatbot'
                            : chat.last_message?.role === 'user'
                            ? 'User Message'
                            : 'Legal Chatbot'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>
                          {chat?.updated_at
                            ? new Date(chat.updated_at).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-20 border border-dashed rounded-md border-gray-300">
                <p className="font-semibold text-sm mb-2">
                  Start by asking a Legal Question
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Drag and Drop Recent Queries <br />
                  to add to Space.
                </p>
                <button
                  onClick={() => navigate('/app/chat')}
                  className="bg-brand hover:bg-brand text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  New Legal Insight
                </button>
              </div>
            )}
          </div>

          {/* === Analysed Documents Section === */}
          <div
            className={`p-5 border rounded-md ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Analysed Documents</h2>
              <hr
                className={`mt-2 ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-300'
                }`}
              />
            </div>

            {hasDocuments ? (
              <ul className="space-y-4">
                {space.contracts.map((doc) => (
                  <li
                    key={doc.contract_id}
                    className={`relative p-4 border rounded-md flex justify-between items-center cursor-pointer transition-all ${
                      isDarkMode
                        ? 'border-gray-700 bg-gray-900 hover:bg-gray-800'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      dispatch(
                        setDocument({
                          fileName: doc.filename,
                          id: doc.contract_id,
                        })
                      );
                      navigate('/app/document/action');
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-indigo-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium truncate">
                          <span title={doc.filename}>
                            {formatName(doc.filename)}
                          </span>
                        </p>
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <p>
                            Uploaded on{' '}
                            {new Date(doc.upload_date).toLocaleDateString()}
                          </p>
                          <p>{doc.analysis_count} analyses</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className="relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() =>
                          setOpenMenu(
                            openMenu === `doc-${doc.contract_id}`
                              ? null
                              : `doc-${doc.contract_id}`
                          )
                        }
                        className="p-1 rounded hover:bg-brand/10 focus:outline-none transition"
                      >
                        <MoreVertical className="w-4 h-4 text-brand" />
                      </button>

                      <AnimatePresence>
                        {openMenu === `doc-${doc.contract_id}` && (
                          <motion.div
                            role="menu"
                            aria-label="Document Options"
                            initial={{ opacity: 0, y: -6, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.98 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className={`absolute right-0 mt-1 w-40 rounded-md shadow-xl border z-[30] ${
                              isDarkMode
                                ? 'bg-gray-900/90 border-gray-700 backdrop-blur-sm'
                                : 'bg-white/90 border-gray-200 backdrop-blur-sm'
                            }`}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenu(null);
                                setConfirmDelete({
                                  type: 'doc',
                                  docId: doc.contract_id,
                                  title: doc.filename,
                                });
                              }}
                              className="w-full text-left px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
              hover:bg-red-500 hover:text-white dark:hover:text-white 
              transition-all duration-150 ease-in-out rounded-md"
                            >
                              Remove from Space
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-20 border border-dashed rounded-md border-gray-300">
                <p className="font-semibold text-sm mb-2">
                  Upload your first document
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Drag and Drop Recent Documents <br /> to add to Space.
                </p>
                <button
                  onClick={() => navigate('/app/document')}
                  className="bg-brand hover:bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  New Document Analysis
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {confirmDelete && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={`w-full max-w-sm rounded-lg shadow-xl p-6 ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold">
                {confirmDelete.type === 'doc'
                  ? '  Remove from Space '
                  : '  Remove from Space '}
              </h3>
            </div>

            <p className="text-sm mb-6">
              Are you sure you want to remove{' '}
              <span
                className="font-medium text-brand inline-block max-w-full truncate"
                title={confirmDelete.title}
              >
                {formatName(confirmDelete.title)}
              </span>{' '}
              <span
                className="font-semibold inline-block max-w-full truncate"
                title={space?.name}
              >
                {formatName(space?.name || 'Unnamed Space')}?
              </span>
              <br />
              {confirmDelete.type === 'doc'
                ? 'This will remove the document from this space. The document will still be available in your documents.'
                : 'This will remove the chat from this space. The chat will still be available in your conversations.'}
            </p>

            <div className="flex justify-end gap-3">
              <button
                disabled={isDeleting}
                onClick={() => setConfirmDelete(null)}
                className={`px-4 py-2 text-sm rounded-md border
    ${
      isDeleting
        ? 'opacity-50 cursor-not-allowed'
        : isDarkMode
        ? 'border-gray-600 hover:bg-gray-700'
        : 'border-gray-300 hover:bg-gray-100'
    }`}
              >
                Cancel
              </button>

              <button
                disabled={isDeleting}
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    if (confirmDelete.type === 'doc') {
                      await deleteContractFromSpace(
                        space.space_id,
                        confirmDelete.docId,
                        uid
                      );

                      setSpace((prev) => ({
                        ...prev,
                        contracts: prev.contracts.filter(
                          (d) => d.contract_id !== confirmDelete.docId
                        ),
                      }));

                      toast.success('Document removed from space.');
                      setConfirmDelete(null);
                    } else {
                      await deleteChatFromSpace(
                        space.space_id,
                        confirmDelete.chatId,
                        uid
                      );

                      setSpace((prev) => ({
                        ...prev,
                        chat_sessions: prev.chat_sessions.filter(
                          (c) => c.chat_session_id !== confirmDelete.chatId
                        ),
                      }));

                      toast.success('Chat removed from space.');
                      setConfirmDelete(null);

                      await dispatch(fetchChatSessions());
                    }
                  } catch (err) {
                    console.error('Delete failed:', err);
                    toast.error(
                      err?.response?.data?.message ||
                        'Failed to delete. Please try again.'
                    );
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                className={`px-4 py-2 text-sm rounded-md flex items-center justify-center gap-2
    ${
      isDeleting
        ? 'bg-red-400 cursor-not-allowed'
        : 'bg-red-500 hover:bg-red-600'
    } text-white transition`}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Removing
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
